/**
 * @engine/vfx — VFX port + node definitions, instances, graph variables.
 */
import type {
  BaseNodeDefinition,
  BaseNodeInstance,
  BasePortDefinition,
  BaseGraphVariable,
  PortKind,
} from '@engine/graph';
import type { VFXDataType } from './data';
import type { VFXContextType } from './contexts';

/**
 * VFX port definition with type-specific metadata.
 */
export interface VFXPortDefinition extends BasePortDefinition {
  /** VFX data type */
  readonly type: VFXDataType;

  /** Port kind: 'flow' for execution, 'data' for values */
  readonly kind: PortKind;

  // `range`, `options`, `variableSelector`, `step`, `control` are inherited from
  // BasePortDefinition (the shared inline-control hints consumed by the editor).
}

/**
 * Helper to create a data port.
 */
export function dataPort(
  id: string,
  name: string,
  type: VFXDataType,
  opts: Partial<Omit<VFXPortDefinition, 'id' | 'name' | 'type' | 'kind'>> = {}
): VFXPortDefinition {
  return { id, name, type, kind: 'data', ...opts } as VFXPortDefinition;
}

/**
 * Helper to create a flow port.
 */
export function flowPort(id: string, name: string): VFXPortDefinition {
  return { id, name, type: 'flow', kind: 'flow' } as VFXPortDefinition;
}

// ============================================================================
// VFX NODE DEFINITION
// ============================================================================

/**
 * VFX node categories.
 */
export type VFXNodeCategory =
  | 'spawn'     // Spawn rate, burst, etc.
  | 'init'      // Initialize particle attributes from events
  | 'attribute' // Set/Get particle attributes
  | 'operator'  // Math, logic, random
  | 'source'    // Position, velocity sources
  | 'force'     // Gravity, turbulence, etc.
  | 'collision' // Collision detection
  | 'output'    // Render modes
  | 'variable'  // Get/Set graph variables
  | 'utility'   // Debug, comment
  | 'sampling'; // Texture sampling, gradients, curves

/**
 * GPU shader implementation of a node — the body of a real **Slang** function.
 * The compiler wraps it into a module-level function and `main()` only wires
 * calls, so locals never collide and `return;` returns from the node.
 *
 * Authoring contract for `code`:
 * - Input ports are read by port id (`force`, `octaves`); output ports are
 *   assigned by port id (`result = a + b;`). The compiler builds the signature:
 *   1 output → return value, N outputs → `out` params, 0 → void.
 * - Gradient/curve ports are **called**: `particle.color = gradient(t);`.
 *   The compiler bakes the instance's data into a sampler function.
 * - Texture ports are `Texture2D<float4>` parameters; use `.Load(int3(x,y,0))`
 *   or the `textureSize(...)` / `loadTexelByIndex(...)` shims from `vfx.common`.
 * - Environment builtins are plain identifiers, detected automatically and
 *   passed as parameters: `particle` (inout), `deltaTime`, `simulationTime`,
 *   `idx`, `localIdx`, `spawnCount`, `sourceEvent`, `emitterVelocity`.
 *   Module-scope bindings (`uniforms`, `vfxVariables`) are directly visible.
 * - Helpers from the `vfx.common` / `vfx.noise` / `vfx.emit` Slang modules
 *   (`hash`, `curlNoise3D`, `applySpread`, …) are always importable — the
 *   linker strips what's unused.
 * - Any valid Slang is allowed, including early `return;` (only for nodes
 *   without data outputs).
 */
export interface VFXNodeShader {
  /** Slang function body (see contract above). */
  readonly code: string;
}

/**
 * VFX node definition.
 */
export interface VFXNodeDefinition extends BaseNodeDefinition<VFXPortDefinition> {
  /** Node category */
  readonly category: VFXNodeCategory;

  /**
   * Which contexts this node can be used in.
   * Empty array = all contexts (operators, etc.)
   */
  readonly contexts: readonly VFXContextType[];

  /**
   * If true, this node affects particle attributes directly.
   */
  readonly modifiesParticle?: boolean;

  /**
   * Render type for output nodes.
   * - 'quad': Standard billboard quad (default)
   * - 'sprite': Flipbook animation
   * - 'point': GPU point sprites
   * - 'line': Velocity-aligned stretched quads
   * - 'trail': Trail ribbons (requires history buffer)
   * - 'mesh': 3D mesh instances
   */
  readonly renderType?: 'quad' | 'sprite' | 'point' | 'line' | 'trail' | 'mesh';

  /**
   * GPU shader implementation — the body of a real Slang function (see
   * {@link VFXNodeShader}). Nodes without one are either structural
   * (spawn/output/trigger/variable getters) or handled specially by the stage
   * generators.
   */
  readonly shader?: VFXNodeShader;

  /**
   * If true, this node cannot be deleted by the user.
   */
  readonly locked?: boolean;

  /**
   * If true, this node needs variable resolution during codegen.
   * Used for dynamic "Get Variable" nodes that reference variables by name.
   */
  readonly requiresVariableResolution?: boolean;
}

// ============================================================================
// VFX NODE INSTANCE
// ============================================================================

/**
 * VFX node instance in a graph.
 */
export interface VFXNodeInstance extends BaseNodeInstance {
  /** Which context this node belongs to (set when adding to graph) */
  contextId?: string;
}

// ============================================================================
// VFX GRAPH VARIABLE
// ============================================================================

/**
 * VFX graph variable (exposed parameters).
 */
export interface VFXGraphVariable extends BaseGraphVariable {
  readonly type: VFXDataType;
  readonly defaultValue: unknown;

  /** If true, can be changed per-instance */
  readonly perInstance?: boolean;
  
  /** For pointmap variables: which channel to sample (position or color) */
  readonly channel?: 'position' | 'color';

  /** UI hints */
  readonly ui?: {
    readonly range?: { min: number; max: number };
    readonly step?: number;
    readonly gradientPreset?: string;
    readonly curvePreset?: string;
  };
}
